/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What a source is worth and what it may run, resolved across the tiers that state it**
 * (frame `0.1` slice `3b`, `#1362`; `docs/SOURCE_TERMS_AND_RESTRICTIONS_DESIGN.md` `ST2`–`ST6c`).
 *
 * ## Two chains, not one, and the difference is the whole module
 *
 * `ST5` applies `CLAUDE.md`'s own *choose then clamp* table **across artifacts** rather than across a
 * precedence list:
 *
 * | | Says | Resolves by | A source may |
 * | --- | --- | --- | --- |
 * | **A default** — cost, price, weight, required and max rounds | *start here; something more specific may override* | last tier stating it wins | **override, in either direction** |
 * | **A bound** — ad restrictions | *never past this, for this context* | union · lowest maximum · highest minimum, applied **after** the chain | **tighten, never loosen** |
 *
 * > The sharp consequence (`ST5`, Todd 2026-09-17): *"may override" is true of a partner's terms and
 * > false of a partner's restrictions.* You can agree a different price with one of a partner's
 * > endpoints. You cannot un-forbid a category their contract forbids.
 *
 * **And the refusal is enforced here, where the value resolves — never only where it is offered.**
 * A bound the authoring surface declines to loosen and the resolver would honour anyway is *a
 * decline the mechanism cannot read* (`#1190`, and `#1313`'s security instance of it). So
 * {@link combineBounds} takes the tighter of the two whatever is stored, and a document written
 * before this rule, imported, or seeded cannot loosen a partner's restriction either.
 *
 * ## The tiers are a list, not a three-level ladder
 *
 * `ST5`'s `allowRepeatAds` case is why: *"a request strategy may decide to block dupes in early
 * rounds then allow later if the pod isn't filling (still can't override a supply source
 * contract)"* — so **a round is a tier**, and a supply source is a tier, and neither is above or
 * below the partner in a fixed ordering that could be written down now. {@link resolveTerms} takes
 * an ordered array, least specific first, and the caller says what the order is. Building it as
 * `standard → partner → source` with three named parameters is the corollary's test failed before
 * the second case arrives.
 *
 * ## Every member has a reader
 *
 * `ST2` names six members — cost, price, weight, required, ad restrictions and max rounds. `3b`
 * carried the ones that had a reader then: `cost` (the optimizer ranks by it, `3c-0`), `required`
 * (both serve paths), and the bounds (`optimizeInput`, after `5a`). Slice `3c-i` added **price** (the
 * drivers' floor and fixed price), **weight** (the rank value, `#937`) and **max rounds** (req-loop's
 * per-source call cap) as **rows in {@link TERM_DEFAULTS}** — the addition the design predicted,
 * with the resolver untouched.
 *
 * **Imports only `./demand-source-cost`**, which is itself shared, so the console can alias this for
 * slice `3c`'s Terms box without dragging the Node-only graph across the boundary
 * (`shared-modules.spec.ts`).
 */

import type { FindingKind } from './finding-kind';
import {
  costValueProblem,
  maxFetchRoundsProblem,
  priceValueProblem,
  readDemandSourceCost,
  readDemandSourcePrice,
  weightValueProblem,
  type DemandSourceCost,
  type DemandSourcePrice,
} from './demand-source-cost';

/* ------------------------------------------------------------------ *
 * Where a resolved value came from
 * ------------------------------------------------------------------ */

/**
 * The tier that stated a value, for the in-force mark.
 *
 * **Required, because `P3a` gives this away and resolution does not** (`ST4`). A parented artifact
 * gets `ArtifactInheritance` — the chip, the revert and the comparison — as a consequence of
 * `extends`. A resolved term gets none of it, and *state what is in force, or say you do not know*
 * is not optional. `ST2` puts the mark's scope at the **box**, which is what makes it affordable:
 * one statement of provenance for a region, with per-member marks inside it.
 */
export interface TermsTierRef {
  /**
   * What kind of thing stated it. `standard` is the seeded **Standard Terms** artifact — the base
   * tier — and it is deliberately **never called *partner* on screen** (`ST6`, `#1045`:
   * `BaselineConfigSection` is already on the Configuration page, so *Base terms* would have
   * collided too).
   */
  kind: 'standard' | 'partner' | 'source' | 'strategy' | 'supply_source' | 'round';
  /** The artifact key, where the tier is an artifact. Absent for a tier that is not one. */
  key?: string;
  /**
   * The tier's **durable** identity — `artifacts.object_id` — where it has one.
   *
   * Carried because `ST6d` stamps the partner onto every metric row **from the resolved source**,
   * at the moment of the event, and `key` is the thing an operator renames. `#1147` measured what a
   * rename does to a business key on a metric row: eleven of them stranded 377 rows under ids that
   * no longer existed. Re-pointing a partner reference would do the mirror image — **re-parent**
   * last quarter's spend onto this quarter's contract — and `ST6a` makes that an expected act,
   * since contract versioning *is* changing the reference.
   *
   * Absent where the tier is not an artifact, and on a chain resolved outside the config read
   * boundary, where no row was read.
   */
  objectId?: string;
  /** What an operator calls it, for the mark's sentence. */
  label: string;
}

/** One tier's contribution: what it states, and nothing about what it does not. */
export interface TermsTier {
  readonly ref: TermsTierRef;
  /** Stated defaults. A member absent here says nothing, and the next tier down answers. */
  readonly terms?: PartialTerms;
  /** Stated bounds. A member absent here imposes nothing (`ST9`: absence means unbounded). */
  readonly bounds?: PartialBounds;
}

/* ------------------------------------------------------------------ *
 * The defaults
 * ------------------------------------------------------------------ */

/**
 * The terms a tier may state. Every member optional: **absence is silence, never a value**.
 *
 * That is `DestinationField.value`'s rule and `FieldCatalogEntry.neverInferred`'s: an unstated
 * default must not be read as *this tier says no cost*, because the tier below it may say one.
 */
export interface PartialTerms {
  /** What we pay the source. `readDemandSourceCost` is the one read of a **stored** one. */
  cost?: DemandSourceCost;
  /** What the auction uses as the gross value — a floor or a fixed price (`ST3`). */
  price?: DemandSourcePrice;
  /** The score boost in selection, a signed percent (`#937`). */
  weight?: number;
  /** Whether the source is required — direct sold, or an inventory split. */
  required?: boolean;
  /** How many times req-loop may call the source per pod. Absent is no cap. */
  maxFetchRounds?: number;
}

/** A member of {@link PartialTerms}, for the in-force map and the exhaustiveness check below. */
export type TermMember = keyof PartialTerms;

/**
 * **The declared member list** — which is what makes `3c` an addition.
 *
 * `satisfies Record<TermMember, true>` is the mechanism: adding a member to {@link PartialTerms}
 * without adding it here is a compile error, and {@link resolveTerms} iterates this rather than
 * naming members, so a new default resolves without anybody editing the resolver. Same pattern and
 * the same reason as `AD_REQUEST_CONTEXT_FIELDS` — *a field a receiver does not declare is a type
 * error nowhere*.
 */
export const TERM_DEFAULTS = {
  cost: true,
  price: true,
  weight: true,
  required: true,
  maxFetchRounds: true,
} as const satisfies Record<TermMember, true>;

export const TERM_MEMBERS: readonly TermMember[] = Object.freeze(Object.keys(TERM_DEFAULTS) as TermMember[]);

/* ------------------------------------------------------------------ *
 * The bounds
 * ------------------------------------------------------------------ */

/**
 * **The ad restrictions a tier may state** — the operator's *ad restrictions*, and the only
 * declaration of them (slice `5c-i`, `#1321`). `AdRestrictionsDto` was a second one with defaults and
 * no author; it is retired, and the pod no longer carries restrictions of its own (`optimizeInput`
 * combines its sources').
 *
 * **Absence means unbounded** (`ST9`), which is why every member is optional and none carries a
 * default here. The 5/60 duration defaults are retired; this module deliberately does not restate
 * them, because a default in the *resolver* would make an unstated bound into a stated one — the
 * thing `ST9` is about — and it would do it below every reader, where nobody would find it.
 *
 * **One member reads the other way, and it is ruled rather than derived:** an unstated
 * {@link allowRepeatAds} means **no repeats** (Todd, 2026-09-21). See the member.
 */
export interface PartialBounds {
  /** The shortest ad this tier will accept. Combined by **max**: the highest minimum wins. */
  minDurationSeconds?: number;
  /** The longest ad this tier will accept. Combined by **min**: the lowest maximum wins. */
  maxDurationSeconds?: number;
  /**
   * **Only these IAB categories may run** — an allow-list. Combined by **intersection**, which is the
   * list spelling of *tighten only*: a tier can narrow what another allows and never widen it.
   *
   * Absent means **every category is allowed** (Todd, 2026-09-21), and that is not the same as an
   * empty list: tiers whose lists share nothing intersect to `[]`, which allows **nothing**, and the
   * Terms box says so rather than rendering it as *no allow-list*.
   *
   * An ad that declares **no** category is not refused by an allow-list — there is nothing to compare,
   * and refusing every uncategorised ad would make a list meant to narrow content into one that
   * empties most pods. This was the rule's behaviour and it is kept deliberately.
   *
   * Was `iabCategories` on `AdRestrictionsDto` and `allowedIabCategories` in the rule's config — one
   * concept under two names (`#1360`). This is the one name, and it pairs with
   * {@link excludeIabCategories} the way the operator reads them.
   */
  allowIabCategories?: readonly string[];
  /** IAB categories this tier forbids — the block list. Combined by **union**. */
  excludeIabCategories?: readonly string[];
  /**
   * Advertiser domains this tier forbids. Combined by **union**, matched against the ad's advertiser
   * domain as a domain rather than a substring (`adDomainExcluded`).
   *
   * **Brands were retired beside this** (`5c-i`, Todd 2026-09-21): an ad carries a domain and no
   * brand, so a brand list could only ever be read as a substring of the domain — `Nike` worked by
   * accident of spelling and `ab.com` blocked `cab.com`.
   */
  excludeAdvertisers?: readonly string[];
  /** Creative domains this tier forbids. Combined by **union**, matched as a domain. */
  excludeDomains?: readonly string[];
  /**
   * Whether the same ad may run twice in one pod — same `adId`, or the same creative by content hash.
   * The two relax **together**: *the same creative twice is fine but the same id is not* is a rule
   * nobody means (`ST7`).
   *
   * Combined by **`false` wins**, which is the boolean spelling of *tighten only*: one tier
   * forbidding repeats is not un-forbidden by another allowing them. `ST7` ruled this
   * **implemented, not retired** (Todd: *the fact that we ignore the flag today is a defect*), and
   * `ST5` names it the case that proves a **round** is a tier — a strategy may block dupes early and
   * relax later, towards the contract and never past it.
   *
   * **Unstated means no repeats** (Todd, 2026-09-21) — the one bound whose absence is the restrictive
   * side. `ST9` read literally would have started serving duplicates on every deployment the day this
   * landed, because *Standard Terms* ships stating nothing; the optimizer had always deduplicated, and
   * that stays the answer until a tier says otherwise. Read it through {@link repeatAdsAllowed}, never
   * as `!== false`, which is exactly the reading this paragraph exists to prevent.
   */
  allowRepeatAds?: boolean;
}

export type BoundMember = keyof PartialBounds;

/** How each bound combines. Declared per member, so a new bound must say which it is. */
export const BOUND_RULES = {
  minDurationSeconds: 'highest',
  maxDurationSeconds: 'lowest',
  allowIabCategories: 'intersection',
  excludeIabCategories: 'union',
  excludeAdvertisers: 'union',
  excludeDomains: 'union',
  allowRepeatAds: 'falseWins',
} as const satisfies Record<BoundMember, BoundRule>;

/** The ways a bound combines. Every one of them can only tighten. */
export type BoundRule = 'highest' | 'lowest' | 'union' | 'intersection' | 'falseWins';

/**
 * **Whether repeat ads are allowed under these bounds** — the one read of {@link PartialBounds.allowRepeatAds}.
 *
 * `true` only where a tier stated `true` and none stated `false`; unstated is **no repeats**.
 */
export function repeatAdsAllowed(bounds: PartialBounds | null | undefined): boolean {
  return bounds?.allowRepeatAds === true;
}

export const BOUND_MEMBERS: readonly BoundMember[] = Object.freeze(Object.keys(BOUND_RULES) as BoundMember[]);

/* ------------------------------------------------------------------ *
 * Resolving
 * ------------------------------------------------------------------ */

/** What one member resolved to, and which tier said so. */
export interface ResolvedTermValue<T> {
  value: T;
  /** The tier the value came from. */
  from: TermsTierRef;
  /**
   * **What the next tier down states, where one does** (`3c-ii`, `#1398`) — the answer if `from` said
   * nothing.
   *
   * The Terms box needs it twice, and both are *state what is in force, or say you do not know*: a
   * source editing its own terms has to show what a blank box would inherit, and a source that
   * overrides has to say what it overrides. Without it the stored source's resolution names only the
   * winner, so an override hides the value it replaced. `P3a` gives a parented field the same thing as
   * the parent's value; resolution has to carry it.
   */
  beneath?: { value: T; from: TermsTierRef };
}

/** What one bound resolved to, and every tier that contributed to it. */
export interface ResolvedBoundValue<T> {
  value: T;
  /**
   * Every tier that contributed — plural, because a bound is a **combination** rather than a choice.
   *
   * A union of three exclusion lists has three authors and naming one of them would be a false
   * claim; a lowest-maximum has one *winner* and the others are still part of the answer an operator
   * is owed when they ask why they cannot raise it.
   */
  from: readonly TermsTierRef[];
  /**
   * **What every tier beneath the most specific one combines to**, where any of them states the member
   * (`5c-i`) — the answer if the most specific tier said nothing.
   *
   * The sibling of {@link ResolvedTermValue.beneath}, and needed for the same two reasons: the box shows
   * what a blank restriction inherits, and a stated one has to be compared against what it tightens
   * (`describeLoosening`). A bound cannot be un-combined afterwards — a union does not say which entries
   * were whose — so the resolver records it while it still has the tiers.
   */
  beneath?: { value: T; from: readonly TermsTierRef[] };
}

/** The terms in force for one source, with the mark `ST2`'s box renders. */
export interface ResolvedTerms {
  terms: { [K in TermMember]?: ResolvedTermValue<NonNullable<PartialTerms[K]>> };
  bounds: { [K in BoundMember]?: ResolvedBoundValue<NonNullable<PartialBounds[K]>> };
  /** The tiers that were consulted, least specific first — what the box's provenance line names. */
  tiers: readonly TermsTierRef[];
}

/**
 * **Resolve one source's terms across its tiers** (`ST5`).
 *
 * `tiers` is ordered **least specific first** — `Standard Terms`, then a partner, then the source —
 * and the caller decides the order, because the tier list is open (`ST5`: a round is a tier, and so
 * is a supply source).
 *
 * Defaults take the **last** tier that states the member. Bounds take the **tighter** of every tier
 * that states it, whichever order they arrive in — which is what makes tighten-only a property of
 * the resolver rather than of the authoring surface.
 */
export function resolveTerms(tiers: readonly TermsTier[]): ResolvedTerms {
  const terms: ResolvedTerms['terms'] = {};
  for (const member of TERM_MEMBERS) {
    let previous: { value: unknown; from: TermsTierRef } | undefined;
    for (const tier of tiers) {
      const stated = tier.terms?.[member];
      if (stated === undefined) continue;
      // Last writer wins, which is most-specific-wins given the documented order. Deliberately not
      // `??` over a reversed list: a tier stating `required: false` is *saying something*, and a
      // nullish-coalescing chain cannot tell that from silence.
      const entry: { value: unknown; from: TermsTierRef; beneath?: { value: unknown; from: TermsTierRef } } = {
        value: stated,
        from: tier.ref,
      };
      if (previous) entry.beneath = previous;
      (terms as Record<string, unknown>)[member] = entry;
      previous = { value: stated, from: tier.ref };
    }
  }

  const bounds = combineBounds(tiers);
  if (tiers.length > 1) {
    const beneath = combineBounds(tiers.slice(0, -1));
    for (const member of BOUND_MEMBERS) {
      const entry = bounds[member];
      const under = beneath[member];
      if (entry && under) (entry as { beneath?: unknown }).beneath = { value: under.value, from: under.from };
    }
  }

  return { terms, bounds, tiers: tiers.map((t) => t.ref) };
}

/**
 * **Combine every tier's bounds, tighter always winning** (`ST5`).
 *
 * Exported on its own because the pod's cross-source union resolves somewhere else entirely
 * (`ST6c`): a source's own bounds resolve at the config read boundary, and the union across the
 * sources actually selected resolves in opt-engine, where the set is known. One function, two
 * callers, so *tighter wins* has one implementation — the `#1269` rule, applied before there are two
 * of them rather than after.
 */
export function combineBounds(tiers: readonly TermsTier[]): ResolvedTerms['bounds'] {
  const out: ResolvedTerms['bounds'] = {};

  for (const member of BOUND_MEMBERS) {
    const stated = tiers.filter((tier) => tier.bounds?.[member] !== undefined);
    if (stated.length === 0) continue;

    const rule = BOUND_RULES[member];
    const from = stated.map((tier) => tier.ref);
    let value: unknown;

    if (rule === 'union') {
      // Case-preserving, case-insensitively deduplicated: `IAB7` and `iab7` are one exclusion, and
      // keeping both would make a list read as longer than the set it forbids. Sorted so the same
      // set of tiers always produces the same list — a bound that reordered per read would show a
      // diff on every save.
      const seen = new Set<string>();
      const entries: string[] = [];
      for (const tier of stated) {
        for (const entry of listEntries(tier.bounds?.[member])) {
          if (seen.has(entry.toLowerCase())) continue;
          seen.add(entry.toLowerCase());
          entries.push(entry);
        }
      }
      value = entries.sort((a, b) => a.localeCompare(b));
    } else if (rule === 'intersection') {
      // What every stating tier allows. The first stating tier's spelling is kept, compared
      // case-insensitively, for the same reason the union does. An empty result is a real answer —
      // *nothing may run* — and is kept as `[]`, never dropped to *unstated*.
      const [first, ...rest] = stated.map((tier) => listEntries(tier.bounds?.[member]));
      const others = rest.map((entries) => new Set(entries.map((e) => e.toLowerCase())));
      const seen = new Set<string>();
      value = first
        .filter((entry) => {
          const key = entry.toLowerCase();
          if (seen.has(key) || !others.every((set) => set.has(key))) return false;
          seen.add(key);
          return true;
        })
        .sort((a, b) => a.localeCompare(b));
    } else if (rule === 'falseWins') {
      value = stated.every((tier) => tier.bounds?.[member] === true);
    } else {
      const numbers = stated
        .map((tier) => Number(tier.bounds?.[member]))
        // A non-finite bound is not a bound. Dropped rather than propagated as `NaN`, which would
        // compare false against everything and quietly disable the check it came from.
        .filter((n) => Number.isFinite(n));
      if (numbers.length === 0) continue;
      value = rule === 'highest' ? Math.max(...numbers) : Math.min(...numbers);
    }

    (out as Record<string, unknown>)[member] = { value, from };
  }

  return out;
}

/** A stored list's entries, trimmed, with blanks dropped. Anything that is not a list states nothing. */
function listEntries(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((raw) => String(raw).trim()).filter((entry) => entry !== '');
}

/**
 * The plain terms an engine reads, without the marks — what a caller that only needs the values
 * takes.
 *
 * Separate from {@link ResolvedTerms} rather than a field on it, because *what is in force* and
 * *where it came from* are answered for different readers: the optimizer wants the first and would
 * carry the second around for nothing, and `TM10`'s lean-runtime question is about exactly this.
 */
export function termValues(resolved: ResolvedTerms): PartialTerms {
  const out: PartialTerms = {};
  for (const member of TERM_MEMBERS) {
    const entry = resolved.terms[member];
    if (entry !== undefined) (out as Record<string, unknown>)[member] = entry.value;
  }
  return out;
}

/** The plain bounds an engine reads. The sibling of {@link termValues}, for the same reason. */
export function boundValues(resolved: ResolvedTerms): PartialBounds {
  const out: PartialBounds = {};
  for (const member of BOUND_MEMBERS) {
    const entry = resolved.bounds[member];
    if (entry !== undefined) (out as Record<string, unknown>)[member] = entry.value;
  }
  return out;
}

/**
 * **Why a set of stated terms cannot be saved**, or `null` when it can (`3c-ii`, `#1398`).
 *
 * One rule for every surface that authors terms — the wizard, the detail page, *Standard Terms* on
 * the Configuration page — and for the server that stores the last of those. An **invalid** value
 * blocks; a **missing** one never does, because absence is silence and the tier below answers
 * (*block on an invalid value, never on a missing one*).
 */
export function termsValueProblem(terms: PartialTerms): string | null {
  if (terms.cost) {
    const problem = costValueProblem(terms.cost.type, terms.cost.value);
    if (problem) return problem;
  }
  return (
    weightValueProblem(terms.weight) ??
    maxFetchRoundsProblem(terms.maxFetchRounds) ??
    priceValueProblem(terms.price?.value) ??
    null
  );
}

/**
 * **Whether a source's stated bound would loosen the tier above it** — the sentence the authoring
 * surface owes (`ST5`).
 *
 * The resolver already refuses to loosen, so this changes nothing about what runs. It exists because
 * *a control that silently refuses half its range is `#1045`'s defect inside the affordance*: slice
 * `3c` renders this beside an Override control so an operator is told *this may be made stricter,
 * not looser* **about the value they just typed**, rather than discovering it from a number that did
 * not move.
 *
 * Returns `null` where the stated value is accepted, tighter or equal.
 */
export function describeLoosening(
  member: BoundMember,
  stated: PartialBounds[BoundMember],
  inherited: ResolvedBoundValue<unknown> | undefined,
): string | null {
  if (stated === undefined || inherited === undefined) return null;
  const above = inherited.from.map((ref) => ref.label).join(', ');
  const rule = BOUND_RULES[member];

  if (rule === 'highest') {
    const n = Number(stated);
    const bound = Number(inherited.value);
    if (!Number.isFinite(n) || !Number.isFinite(bound) || n >= bound) return null;
    return `${above} requires at least ${bound}s, so ${n}s is not applied — a bound may be made stricter, not looser.`;
  }
  if (rule === 'lowest') {
    const n = Number(stated);
    const bound = Number(inherited.value);
    if (!Number.isFinite(n) || !Number.isFinite(bound) || n <= bound) return null;
    return `${above} allows at most ${bound}s, so ${n}s is not applied — a bound may be made stricter, not looser.`;
  }
  if (rule === 'falseWins') {
    if (stated !== true || inherited.value !== false) return null;
    return `${above} forbids repeat ads, so allowing them here has no effect — a bound may be made stricter, not looser.`;
  }
  if (rule === 'intersection') {
    const allowed = new Set(listEntries(inherited.value).map((e) => e.toLowerCase()));
    const beyond = listEntries(stated).filter((entry) => !allowed.has(entry.toLowerCase()));
    if (beyond.length === 0) return null;
    return `${above} does not allow ${beyond.join(', ')}, so ${beyond.length === 1 ? 'it is' : 'they are'} not applied — a bound may be made stricter, not looser.`;
  }

  // A union never loosens: adding exclusions can only narrow what may run, and a tier cannot remove
  // another tier's entry because the combination has no representation for a removal.
  return null;
}

/**
 * **Why a set of stated restrictions cannot be saved**, or `null` when it can — the sibling of
 * {@link termsValueProblem}, for every surface that authors restrictions and the server behind them.
 *
 * Blocks only an **invalid** value: a negative or non-numeric duration, or a minimum above the
 * maximum *the same tier* states. A minimum above a maximum another tier states is not this tier's
 * mistake, and the box reports the combination instead (*block on an invalid value, never on a
 * missing one*).
 */
export function boundsValueProblem(bounds: PartialBounds | null | undefined): string | null {
  return boundsValueFinding(bounds)?.message ?? null;
}

/**
 * {@link boundsValueProblem} with **what kind of problem** it is (`3i`, `#1412`), for a surface that
 * shows the kind — the restriction list's pill. The sentence is the same one; the server, which only
 * needs to refuse, keeps reading the string.
 */
export function boundsValueFinding(
  bounds: PartialBounds | null | undefined,
): { kind: FindingKind; message: string } | null {
  if (!bounds) return null;
  const { minDurationSeconds: min, maxDurationSeconds: max } = bounds;
  for (const [label, value] of [
    ['Minimum ad length', min],
    ['Maximum ad length', max],
  ] as const) {
    if (value === undefined) continue;
    const numeric = typeof value === 'number' && Number.isFinite(value);
    if (!numeric || value < 0) {
      return {
        kind: numeric ? 'out-of-range' : 'wrong-type',
        message: `${label} must be a number of seconds, zero or more.`,
      };
    }
  }
  if (min !== undefined && max !== undefined && min > max) {
    return {
      kind: 'min-over-max',
      message: `Minimum ad length (${min}s) is longer than the maximum (${max}s), so no ad could run.`,
    };
  }
  for (const member of BOUND_MEMBERS) {
    const rule = BOUND_RULES[member];
    if (rule !== 'union' && rule !== 'intersection') continue;
    const value = bounds[member];
    if (value !== undefined && !Array.isArray(value))
      return { kind: 'wrong-type', message: `${member} must be a list.` };
  }
  if (bounds.allowRepeatAds !== undefined && typeof bounds.allowRepeatAds !== 'boolean') {
    return { kind: 'wrong-type', message: 'Repeat ads must be allowed or not allowed.' };
  }
  return null;
}

/**
 * **The declared members a stored restrictions object states**, and nothing else — the one read of a
 * stored `restrictions`, for the tier builder and every write that stores one.
 *
 * A key outside {@link BOUND_RULES} is dropped rather than carried, so a retired member
 * (`excludeBrands`, `maxAdsPerPod`, `allowedAdTypes`) or a typo cannot be stored as a restriction
 * nothing enforces — `#1017`'s family at the write boundary. Lists are trimmed and emptied of blanks,
 * and an **empty exclusion list** states nothing, while an **empty allow-list** is kept: it says
 * *nothing may run*, which is a statement.
 */
export function statedBounds(stored: unknown): PartialBounds | undefined {
  if (!stored || typeof stored !== 'object') return undefined;
  const record = stored as Record<string, unknown>;
  const out: PartialBounds = {};
  for (const member of BOUND_MEMBERS) {
    const value = record[member];
    if (value === undefined || value === null) continue;
    const rule = BOUND_RULES[member];
    if (rule === 'union' || rule === 'intersection') {
      if (!Array.isArray(value)) continue;
      const entries = listEntries(value);
      if (entries.length === 0 && rule === 'union') continue;
      (out as Record<string, unknown>)[member] = entries;
    } else if (rule === 'falseWins') {
      if (typeof value === 'boolean') out.allowRepeatAds = value;
    } else {
      const n = finiteNumber(value);
      if (n !== undefined) (out as Record<string, unknown>)[member] = n;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/* ------------------------------------------------------------------ *
 * The tiers, and the one read of what is in force
 * ------------------------------------------------------------------ */

/**
 * The artifact key of the seeded base tier (`ST6`).
 *
 * Here rather than on `PartnerDto` because **this is the file the read side can reach**: an engine
 * reader lives in `libs/drivers`, which does not import `libs/config` and must not import a DTO that
 * drags the artifact base in. `partner.dto.ts` imports it back, so there is one home and the arrow
 * runs one way.
 */
export const STANDARD_TERMS_KEY = 'standard-terms';

/** What an operator is shown. Deliberately **not** the word *partner* (`ST6`, `#1045`). */
export const STANDARD_TERMS_NAME = 'Standard Terms';

/**
 * Where a resolved answer is attached on a document.
 *
 * A constant because three things address it — the config read boundary writes it, `toArtifactWrite`
 * strips it, and every engine reader takes it through {@link effectiveTerms} — and three spellings
 * of one key is `#1003`'s failure with the obviousness removed.
 */
export const RESOLVED_TERMS_KEY = 'resolvedTerms';

/**
 * **A document whose terms can be resolved** — structural, so no caller needs a cast.
 *
 * Declared as the shape the resolution actually reads rather than as `DemandSourceDto`, for two
 * reasons: this file must import nothing but `demand-source-cost` to stay aliasable by the console,
 * and supply source (`#826`) is the second type to pass through here — taking the concrete DTO would
 * make that a **change** rather than an addition.
 *
 * `terms` is `unknown` because the one read of a stored cost is `readDemandSourceCost`, which
 * already handles every spelling a document may carry. Narrowing it here would be a second opinion
 * about the stored shape.
 */
export interface TermsBearingDocument {
  id?: string;
  name?: string;
  /** The partner this document buys under. Read here; written by no surface in `0.1` (`ST6b`). */
  partnerId?: string;
  terms?: unknown;
  /** The ad restrictions this document states — read through {@link statedBounds}. */
  restrictions?: unknown;
  /**
   * Attached by the config read boundary. Spelled out rather than computed from
   * {@link RESOLVED_TERMS_KEY} because an interface key must be a literal — and
   * `artifact-terms-resolution.spec.ts` asserts the two agree, which is the arrangement this
   * codebase accepts for a pair that cannot be derived from each other.
   */
  resolvedTerms?: ResolvedTerms;
}

/** A partner as the tier builder needs it: its key, its name, and the two things it states. */
export interface PartnerTier {
  readonly key: string;
  readonly name: string;
  /** `artifacts.object_id` — what a metric row is stamped with (`ST6d`). */
  readonly objectId?: string;
  readonly terms?: PartialTerms;
  readonly bounds?: PartialBounds;
}

/**
 * The tiers for one source, **least specific first** — the order {@link resolveTerms} documents and
 * the caller owns.
 *
 * *Standard Terms* is placed by its key rather than by a flag, and it is skipped when the source's
 * own `partnerId` names it, so a source pointing at the base tier does not contribute it twice. A
 * duplicate tier is harmless for a default and **wrong in the mark**: the box would name one tier
 * twice and read as though two parties had agreed the value.
 */
export function tiersFor(
  document: TermsBearingDocument | null | undefined,
  partners: ReadonlyMap<string, PartnerTier>,
): TermsTier[] {
  const doc = document ?? {};
  const tiers: TermsTier[] = [];

  const standard = partners.get(STANDARD_TERMS_KEY);
  if (standard) {
    tiers.push({
      ref: { kind: 'standard', key: standard.key, objectId: standard.objectId, label: STANDARD_TERMS_NAME },
      terms: standard.terms,
      bounds: statedBounds(standard.bounds),
    });
  }

  const partnerId = typeof doc.partnerId === 'string' ? doc.partnerId.trim().toLowerCase() : '';
  if (partnerId && partnerId !== STANDARD_TERMS_KEY) {
    const partner = partners.get(partnerId);
    // A `partnerId` naming nothing contributes no tier, and that is **not** an error here: `A6`'s
    // graph is what reports a dangling reference, and throwing would take a whole demand source
    // collection down because somebody deleted a partner.
    if (partner) {
      tiers.push({
        ref: { kind: 'partner', key: partner.key, objectId: partner.objectId, label: partner.name },
        terms: partner.terms,
        bounds: statedBounds(partner.bounds),
      });
    }
  }

  tiers.push({
    ref: {
      kind: 'source',
      key: String(doc.id ?? ''),
      label: String(doc.name ?? doc.id ?? 'this source'),
    },
    terms: statedTerms(doc),
    // Slice `5c-i`: the one line `3b` said it would be.
    bounds: statedBounds(doc.restrictions),
  });

  return tiers;
}

/**
 * What the source itself states.
 *
 * `cost` goes through {@link readDemandSourceCost}, which is **the one read of a stored cost** — a
 * pre-`3c-0` document holds a bare number beside a `model` nothing read, and a second reading of
 * that shape here is exactly the `#1269` defect. `price` goes through {@link readDemandSourcePrice}
 * for the same reason.
 *
 * **`required` is a statement only when it is stored.** It was declared `required: boolean = false`
 * until slice `3c-i`, so every write gave a source that stated nothing an explicit `false` — read here
 * as *the source says it is not required*, which beat a partner stating `true`. The field is optional
 * now, the console writes only a statement, and the fold drops the `false` a parentless document
 * carried as the old default (`foldRetiredTermFields`).
 */
function statedTerms(doc: TermsBearingDocument): PartialTerms | undefined {
  const stored = doc.terms as
    | { required?: unknown; weight?: unknown; maxFetchRounds?: unknown; price?: unknown }
    | undefined;
  const terms: PartialTerms = {};
  const cost = readDemandSourceCost(doc.terms as never);
  if (cost !== undefined) terms.cost = cost;
  const price = readDemandSourcePrice(stored?.price);
  if (price !== undefined) terms.price = price;
  const weight = finiteNumber(stored?.weight);
  if (weight !== undefined) terms.weight = weight;
  if (typeof stored?.required === 'boolean') terms.required = stored.required;
  const rounds = finiteNumber(stored?.maxFetchRounds);
  if (rounds !== undefined && rounds >= 0) terms.maxFetchRounds = Math.floor(rounds);
  return Object.keys(terms).length > 0 ? terms : undefined;
}

/** A stored number, or nothing. A string that parses is a number an import stringified. */
function finiteNumber(value: unknown): number | undefined {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
}

/**
 * **What is in force for this source** — the one read, for every engine and every surface.
 *
 * Falls back to resolving the source's own tier alone where nothing attached an answer. That is not
 * a second resolution rule: it is the same {@link resolveTerms} over a one-tier list, and it exists
 * because a `DemandSourceDto` built in a spec, arrived over an RPC boundary, or assembled by the
 * test rig has never passed the config read boundary. Reading `terms` directly in that case is the
 * second reader `#1269` is about — the one narrower than its writer, which returns nothing and is
 * never reported.
 */
export function effectiveTerms(source: TermsBearingDocument | null | undefined): ResolvedTerms {
  const attached = source?.resolvedTerms;
  if (attached) return attached;
  return resolveTerms(tiersFor(source, EMPTY_PARTNERS));
}

const EMPTY_PARTNERS: ReadonlyMap<string, PartnerTier> = new Map();

/**
 * **What we pay this source, after resolution** — the accessor every engine reader takes.
 *
 * `readDemandSourceCost(ds.terms)` answers *what this document stated*, which stopped being the
 * whole answer the moment a tier above it could state one too. This is the reader that sees a
 * partner's cost; the other is the reader that cannot, and leaving both in use is `#1269` with the
 * narrow one still wired up.
 */
export function effectiveCost(source: TermsBearingDocument | null | undefined): DemandSourceCost | undefined {
  return effectiveTerms(source).terms.cost?.value;
}

/** Whether this source is required, after resolution. The sibling of {@link effectiveCost}. */
export function effectiveRequired(source: TermsBearingDocument | null | undefined): boolean {
  return effectiveTerms(source).terms.required?.value === true;
}

/** What the auction uses as this source's gross value, after resolution. */
export function effectivePrice(source: TermsBearingDocument | null | undefined): DemandSourcePrice | undefined {
  return effectiveTerms(source).terms.price?.value;
}

/** This source's score boost, a signed percent, after resolution. `undefined` is no boost. */
export function effectiveWeight(source: TermsBearingDocument | null | undefined): number | undefined {
  return effectiveTerms(source).terms.weight?.value;
}

/** How many times this source may be called per pod, after resolution; opt-engine applies it when it chooses a round's sources (slice 5). `undefined` is no cap. */
export function effectiveMaxFetchRounds(source: TermsBearingDocument | null | undefined): number | undefined {
  return effectiveTerms(source).terms.maxFetchRounds?.value;
}

/**
 * **The ad restrictions in force for this source**, after resolution — what it contributes to a pod's
 * union (`ST6c`) and what `demandSource.restrictions` resolves to on a mapping.
 */
export function effectiveBounds(source: TermsBearingDocument | null | undefined): PartialBounds {
  return boundValues(effectiveTerms(source));
}

/**
 * **The partner this source buys under, for a metric row's stamp** (`ST6d`).
 *
 * The `partner` tier only — *Standard Terms* is deliberately not a partner and must never be
 * attributed as one. `N5` defines a partner as a commercial counterparty and the base tier is us,
 * so stamping it would put every unattached source's spend under a counterparty that does not
 * exist, on the one screen where the number is read as a commercial fact.
 *
 * Returns `undefined` where the source names no partner, which is *no partner*, not *unknown*.
 */
export function effectivePartner(source: TermsBearingDocument | null | undefined): TermsTierRef | undefined {
  return effectiveTerms(source).tiers.find((tier) => tier.kind === 'partner');
}
